import { ReactElement, useState } from "react";
import { t } from "@lingui/core/macro";

import {
  Attempt,
  Project,
  Revision,
  useProjectAttempts,
  useRevisionResources,
  useAttemptDatasetManifest,
  AttemptDatasetManifest,
} from "_/data/projects";
import { blobDownloadURL, blobKindLabel, BlobRecord } from "_/data/blob";
import { useMachine } from "_/data/machines";
import { useUser } from "_/data/users";
import { useMaterials } from "_/data/materials";

import { Tuple, Uuid } from "_/types";

import { Table, ColumnDef } from "_/components/table";
import { StatusBadge } from "_/components/badge";
import { Button, ButtonGroup } from "_/components/button";
import { AttemptDataDownloadList } from "_/components/attempt-data-download-list";
import { Icon, IconVariant } from "_/components/icon";
import { Modal } from "_/components/modal";
import { DateTime } from "_/components/date-time";
import { MaterialName } from "_/components/materials";
import { Link, NeverUnderlineSpace } from "_/components/router";
import { FileDownloadLink } from "_/components/file-download-link";
import { NumberBadge } from "_/components/number-badge";

import { machineUrls, projectUrls } from "_/routes";
import { formatBytes } from "_/utils";
import { dateTimeString } from "_/utils/datetime";

import * as S from "./styled";

/**
 * Helper component for rendering "Machine" column of an Attempts entry.
 */
const MachineCell = ({ machineId }: { machineId: Uuid }): ReactElement => {
  const { data: machine } = useMachine(machineId);

  return <Link to={machineUrls.index(machineId)}>{machine?.name}</Link>;
};

type ModalProps = {
  attemptId: Uuid;
  manifest: AttemptDatasetManifest[];
};

/**
 * Helper component for rendering "Data" column of an Attempts entry.
 */
const DataCell = ({
  revision,
  attempt,
  setModalProps,
}: {
  revision: Revision;
  attempt: Attempt;
  setModalProps: (modalProps: ModalProps) => void;
}): ReactElement => {
  const manifestQuery = useAttemptDatasetManifest(
    { projectId: revision.projectId, attempt },
    { enabled: false }
  );

  return (
    <ButtonGroup>
      {attempt.endedAt && (
        <Button
          size="small"
          kind="secondary"
          onMouseOver={() => {
            // Only attempt to load the data if it hasn't been yet.
            if (manifestQuery.isStale || !manifestQuery.data) {
              void manifestQuery.refetch();
            }
          }}
          onClick={() =>
            setModalProps({
              attemptId: attempt.id,
              manifest: manifestQuery.data?.manifest ?? [],
            })
          }
        >
          {t`common.download`}
        </Button>
      )}
      <Link to={projectUrls.analyze(revision.projectId, attempt.id)}>
        <Button size="small">{t`common.analyze`}</Button>
      </Link>
    </ButtonGroup>
  );
};

const RevisionMeta = ({ revision }: { revision: Revision }): ReactElement => {
  const { data: user } = useUser({ id: revision.creatorId });

  const createdAtString = dateTimeString({ value: revision.createdAt });
  const createdByString = user ? user.name : t`common.unknown`;

  return (
    <S.RevisionMeta>
      <span>
        {t`common.created`}{" "}
        {t`common.created-at-and-by ${createdAtString} ${createdByString}`}
      </span>
    </S.RevisionMeta>
  );
};

const MaterialsCards = ({
  materialIds: [materialId0, materialId1],
}: {
  materialIds: Tuple<string | null>;
}): ReactElement | null => {
  const { data: materials } = useMaterials();

  const material0 = materials?.find((m) => m.id === materialId0);
  const material1 = materials?.find((m) => m.id === materialId1);

  if (!material0 && !material1) {
    return null;
  }

  return (
    <S.MaterialsGrid>
      {material0 && (
        <S.MaterialCard>
          <S.MaterialCardLabel>{t`common.primary-material`}</S.MaterialCardLabel>
          <MaterialName material={material0} />
        </S.MaterialCard>
      )}
      {material1 && (
        <S.MaterialCard>
          <S.MaterialCardLabel>{t`common.secondary-material`}</S.MaterialCardLabel>
          <MaterialName material={material1} />
        </S.MaterialCard>
      )}
    </S.MaterialsGrid>
  );
};

// Icon shown next to each file, indicating the kind of file it is.
function fileIconVariant(kind: BlobRecord["kind"]): IconVariant {
  switch (kind) {
    case "gcode":
      return "Code";
    case "stl":
    case "3mf":
      return "ViewInAr";
    case "unknown":
    default:
      return "InsertDriveFile";
  }
}

const FileRow = ({ file }: { file: BlobRecord }) => {
  return (
    <S.FileRow>
      <S.FileIcon>
        <Icon
          variant={fileIconVariant(file.kind)}
          label={blobKindLabel(file.kind)}
        />
      </S.FileIcon>
      <S.FileKindTag>{blobKindLabel(file.kind)}</S.FileKindTag>
      <FileDownloadLink
        href={blobDownloadURL(file.id)}
        title={file.name}
        variant="plain"
      >
        <S.FileName>{file.name}</S.FileName>
      </FileDownloadLink>
      <S.FileSize>{formatBytes(file.uncompressedSize)}</S.FileSize>
    </S.FileRow>
  );
};

const RevisionHeaderAndLink = ({
  project,
  revision,
}: {
  project: Project;
  revision: Revision;
}) => {
  return (
    <S.RevisionLink
      id={revision.id}
      to={projectUrls.prepare(project.id, revision.id)}
    >
      <span>{t`common.revision`}</span>
      <NeverUnderlineSpace />
      <NumberBadge number={revision.number} size="lg" />
    </S.RevisionLink>
  );
};

const fileSorter = (a: BlobRecord, b: BlobRecord) => {
  // Sort G-code first, model files (STL/3MF) second, and everything else last.
  // Within each group, sort alphabetically by name.
  const kindOrder = ["gcode", "stl", "3mf"];
  const [aKindRank, bKindRank] = [a.kind, b.kind].map((kind) => {
    const rank = kindOrder.indexOf(kind);
    return rank !== -1 ? rank : kindOrder.length; // other kinds are last
  });
  const kindRank = aKindRank - bKindRank;
  if (kindRank) return kindRank;

  return a.name.localeCompare(b.name);
};

const OneRevision = ({
  project,
  revision,
  attempts,
  attemptsLoading,
  setModalProps,
}: {
  project: Project;
  revision: Revision;
  attempts: Attempt[];
  attemptsLoading: boolean;
  setModalProps: (props: ModalProps) => void;
}) => {
  const { data: files = [] } = useRevisionResources(revision);

  const sortedFiles = files.toSorted(fileSorter);

  const jobNumber = (attempt: Attempt) =>
    attempts.length - attempts.indexOf(attempt);
  const jobDescription = (attempt: Attempt) =>
    t`common.job-description ${revision.number} ${jobNumber(attempt)}`;

  const columns: ColumnDef<Attempt>[] = [
    {
      header: t`common.job`,
      cell: ({ row: { original: rowData } }) => (
        <S.JobNumber title={jobDescription(rowData)}>
          {`#${revision.number}-${jobNumber(rowData)}`}
        </S.JobNumber>
      ),
    },
    {
      header: t`common.machine`,
      cell: ({ row: { original: rowData } }) => (
        <MachineCell machineId={rowData.machineId} />
      ),
    },
    {
      accessorKey: "startedAt",
      header: t`common.start-date`,
      cell: ({ row: { original: rowData } }) => (
        <DateTime value={rowData.startedAt} />
      ),
    },
    {
      accessorKey: "endedAt",
      header: t`common.end-date`,
      cell: ({ row: { original: rowData } }) => (
        <DateTime value={rowData.endedAt} />
      ),
    },
    {
      header: t`common.status`,
      cell: ({ row: { original: rowData } }) => {
        return <StatusBadge status={rowData.status} />;
      },
    },
    {
      header: t`components.attempts-listing.data`,
      cell: ({ row: { original: rowData } }) => (
        <DataCell
          revision={revision}
          attempt={rowData}
          setModalProps={setModalProps}
        />
      ),
    },
  ];

  return (
    <S.OneRevision>
      <S.TopRow>
        <S.RevisionInfo>
          <S.RevisionHeading>
            <RevisionHeaderAndLink project={project} revision={revision} />
          </S.RevisionHeading>
          <RevisionMeta revision={revision} />
        </S.RevisionInfo>
        <MaterialsCards materialIds={revision.materials} />
      </S.TopRow>
      {sortedFiles.length > 0 && (
        <S.FilesSection>
          <S.FilesList>
            {sortedFiles.map((f) => (
              <FileRow key={f.id} file={f} />
            ))}
          </S.FilesList>
        </S.FilesSection>
      )}
      <Table
        columns={columns}
        data={attempts ?? []}
        loading={attemptsLoading}
        skeletonRowCount={1}
        renderNoData={() => (
          <S.NoAttemptsContainer>
            <S.NoAttemptsIcon>
              <Icon variant="Info" />
            </S.NoAttemptsIcon>
            {t`common.no-attempts`}
          </S.NoAttemptsContainer>
        )}
      />
    </S.OneRevision>
  );
};

export const AttemptsListing = ({
  project,
}: {
  project: Project;
}): ReactElement | null => {
  const [modalProps, setModalProps] = useState<ModalProps | null>(null);
  const { data: attempts, isPending: attemptsLoading } = useProjectAttempts(
    project.id
  );
  const { revisions } = project;

  if (!revisions) return null;

  return (
    <S.Wrapper>
      <>
        {revisions.toReversed().map((revision) => (
          <OneRevision
            key={revision.id}
            project={project}
            revision={revision}
            attempts={(attempts ?? []).filter(
              (a) => a.revisionId === revision.id
            )}
            attemptsLoading={attemptsLoading}
            setModalProps={setModalProps}
          />
        ))}
        <Modal
          title={t`components.attempts-listing.downloads`}
          open={modalProps !== null}
          onClose={() => {
            setModalProps(null);
          }}
        >
          {modalProps && (
            <AttemptDataDownloadList
              projectId={project.id}
              attemptId={modalProps.attemptId}
              manifest={modalProps.manifest}
            />
          )}
        </Modal>
      </>
    </S.Wrapper>
  );
};
